/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, September 8th 2022, 3:52:28 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { Node, Edge, Viewport, useNodesState, useEdgesState, OnNodesChange, OnEdgesChange } from "react-flow-renderer";
import { TaskTemplate } from "lib/schemas";
import { IFlowChartAdapter } from "service-builder/adapters/types";
import { useServiceBuilderContext } from "service-builder/providers";

interface IReactFlowContext {
  nodes: Node<TaskTemplate>[];
  edges: Edge<TaskTemplate>[];
  defaultViewportState: Viewport;
  setNodes: React.Dispatch<React.SetStateAction<Node<TaskTemplate>[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge<TaskTemplate>[]>>;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  persistViewPortState: (viewport: Viewport) => void;
  persistNodePosition: (args: { taskTemplate: TaskTemplate; x: number; y: number }) => void;
}

export const ReactFlowContext = React.createContext<IReactFlowContext>({
  nodes: [],
  edges: [],
  defaultViewportState: null,
  setNodes: () => {},
  setEdges: () => {},
  onNodesChange: (changes) => {},
  onEdgesChange: (changes) => {},
  persistViewPortState: () => {},
  persistNodePosition: () => {},
});

export const useReactFlowContext = () => {
  return React.useContext(ReactFlowContext);
};

export const useReactFlowAdapter: IFlowChartAdapter<IReactFlowContext> = ({
  taskTemplateListContext: { filteredItems, updateList, findItem },
  patchTaskTemplate,
}) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
    patchCurrentProcessDefinition,
  } = useServiceBuilderContext();

  const [nodes, setNodes, onNodesChange] = useNodesState(reactFlowNodesAdapter(filteredItems));
  const [edges, setEdges, onEdgesChange] = useEdgesState(reactFlowEdgesAdapter(filteredItems));

  React.useEffect(() => {
    setNodes((prevNodes) => reactFlowNodesAdapter(filteredItems, prevNodes));
    setEdges(reactFlowEdgesAdapter(filteredItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filteredItems)]);

  const persistViewPortState = React.useCallback<IReactFlowContext["persistViewPortState"]>(
    (viewport: Viewport) => {
      const [x_position_factor, y_position_factor] = ElementPositionFactor.toFactor(viewport?.x, viewport?.y);

      patchCurrentProcessDefinition({
        extra_data: {
          ...currentProcessDefinition?.extra_data,
          flowchart_viewport_state: { x_position_factor, y_position_factor, zoom_level: 1 },
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(currentProcessDefinition?.extra_data)]
  );

  const getDefaultViewportState = (): Viewport => {
    const [x, y] = ElementPositionFactor.toPosition(
      currentProcessDefinition?.extra_data?.flowchart_viewport_state?.x_position_factor,
      currentProcessDefinition?.extra_data?.flowchart_viewport_state?.y_position_factor
    );

    return {
      x,
      y,
      zoom: currentProcessDefinition?.extra_data?.flowchart_viewport_state?.zoom_level,
    };
  };

  const persistNodePosition = React.useCallback<IReactFlowContext["persistNodePosition"]>(
    ({ taskTemplate, x, y }) => {
      if (!taskTemplate?.id) return;
      patchTaskTemplate({
        apiPathParams: [taskTemplate.id],
        body: JSON.stringify({
          extra_data: {
            ...taskTemplate?.extra_data,
            flowchart_position: { x_position_factor: x, y_position_factor: y },
          },
        }),
        setValidAPIResponse: (apiResponse) => updateList(apiResponse),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    nodes,
    edges,
    defaultViewportState: getDefaultViewportState(),
    setNodes,
    onNodesChange,
    setEdges,
    onEdgesChange,
    persistViewPortState,
    persistNodePosition,
  };
};

const ElementPositionFactor = {
  toFactor: (elementXPosition: number, elementYPosition: number) => {
    return [elementXPosition / window.innerWidth, elementYPosition / window.innerHeight];
  },
  toPosition: (elementXPositionFactor: number, elementYPositionFactor: number) => {
    return [elementXPositionFactor ?? 0 * window.innerWidth, elementYPositionFactor ?? 0 * window.innerHeight];
  },
};

const reactFlowNodesAdapter = (
  taskTemplates: TaskTemplate[],
  reactFlowNodes?: Node<TaskTemplate>[]
): Node<TaskTemplate>[] => {
  let xPosition: number;
  let yPosition: number;
  let previousSplitNode: TaskTemplate;
  let outgoingNodeXFactor: number = -5;

  return taskTemplates.map((taskTemplate) => {
    const { x_position_factor, y_position_factor } = taskTemplate?.extra_data?.flowchart_position ?? {};

    const existingReactFlowNode = reactFlowNodes?.find((node) => node.data.id === taskTemplate.id);

    if (existingReactFlowNode?.position) {
      xPosition = existingReactFlowNode?.position?.x;
      yPosition = existingReactFlowNode?.position?.y;
    } else if (x_position_factor && y_position_factor) {
      xPosition = x_position_factor;
      yPosition = y_position_factor;
    } else if (
      previousSplitNode?.type === "Split" &&
      previousSplitNode?.outgoing_branches.map((outgoing_summary) => outgoing_summary.next_id).includes(taskTemplate.id)
    ) {
      outgoingNodeXFactor = outgoingNodeXFactor + 5;
      xPosition = 125 * outgoingNodeXFactor;
      yPosition = (previousSplitNode.sequence + 1) * 100;
    } else {
      xPosition = 250;
      outgoingNodeXFactor = -5;
      yPosition = taskTemplate.sequence * 100;
    }

    if (taskTemplate.type === "Split") {
      previousSplitNode = taskTemplate;
    }

    return {
      id: `${taskTemplate.id}`,
      data: taskTemplate,
      type: "taskTemplate",
      position: { x: xPosition, y: yPosition },
    };
  });
};

const reactFlowEdgesAdapter = (taskTemplates: TaskTemplate[]): Edge<TaskTemplate>[] => {
  return taskTemplates.reduce((cumulativeEdgesList: Edge<TaskTemplate>[], currentTaskTemplate: TaskTemplate) => {
    let currentEdgesList: Edge<TaskTemplate>[] = [];

    switch (currentTaskTemplate.type) {
      case "Split":
        currentEdgesList = currentTaskTemplate.outgoing_branches?.map((outgoing_summary) => ({
          id: `${currentTaskTemplate.id}-${outgoing_summary.next_id}`,
          source: `${currentTaskTemplate.id}`,
          target: `${outgoing_summary.next_id}`,
          type: "taskTemplateNodeEdge",
        }));
        break;
      default:
        currentEdgesList = [
          {
            id: `${currentTaskTemplate.id}-${currentTaskTemplate.next}`,
            source: `${currentTaskTemplate.id}`,
            target: `${currentTaskTemplate.next}`,
            type: "taskTemplateNodeEdge",
          },
        ];
    }

    return cumulativeEdgesList.concat(currentEdgesList);
  }, []);
};
